import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState } from "react";
import { RulesContext } from "@/context/RulesContext";
import { Rule } from "@/pages/Messaging/Rules/types";
import useActivityLog from "@/hooks/useActivityLog";

export function RulesProvider(props: { children: React.ReactNode }) {
  const api = useApi();
  const { track } = useActivityLog();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const json = await api.messaging.get("Rules/List");
      const result = (await json.json()) as Rule[];
      setRules(Array.isArray(result) ? result : []);
    } catch {
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  function saveVersionSnapshot(rule: Rule, changeType: "Created" | "Updated" | "Deleted") {
    // Fire-and-forget — version save never blocks UI
    api.admin.post("RuleVersions", {
      ruleId: rule.id,
      trigger: rule.trigger,
      priority: rule.priority,
      ruleJson: JSON.stringify(rule.expression),
      valueJson: JSON.stringify(rule.value),
      changeType
    }).catch((e) => console.warn("[RuleVersions] snapshot failed (non-fatal):", e));
  }

  async function remove(rule: Rule) {
    const clone = [...rules];

    if (rule.id === undefined) {
      //hasn't been saved yet
      setRules(clone);
      return;
    }

    const i = rules.findIndex((x) => x.id === rule.id);
    clone.splice(i, 1)[0];

    try {
      const res = await api.messaging.delete("Rules", rule);
      if (res.ok) {
        setRules(clone);
        track({
          category: "Rules",
          action: "RuleDeleted",
          entityType: "Rule",
          entityId: String(rule.id),
          detail: { trigger: rule.trigger }
        });
        saveVersionSnapshot(rule, "Deleted");
      } else {
        console.error(res.statusText);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function add(rule: Rule) {
    setSaving(true);
    try {
      const res = await api.messaging.post("Rules", rule);
      if (res.ok) {
        const data = await res.json();
        setRules((prev) => [...prev, data]);
        track({
          category: "Rules",
          action: "RuleCreated",
          entityType: "Rule",
          entityId: String(data?.id ?? ""),
          detail: { trigger: rule.trigger }
        });
        saveVersionSnapshot({ ...rule, id: data?.id }, "Created");
      } else {
        console.error(res.statusText);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  async function update(rule: Rule) {
    setSaving(true);
    try {
      const res = await api.messaging.put("Rules", rule);
      if (res.ok) {
        const data = await res.json();
        const clone = [...rules];
        const i = rules.findIndex((x) => x.id === rule.id);
        clone[i] = data;
        setRules(clone);
        track({
          category: "Rules",
          action: "RuleUpdated",
          entityType: "Rule",
          entityId: String(rule.id ?? ""),
          detail: { trigger: rule.trigger }
        });
        saveVersionSnapshot(rule, "Updated");
      } else {
        console.error(res.statusText);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <RulesContext.Provider
      value={{ rules: rules, saving, loading, add, update, remove, refresh: fetch }}
    >
      {props.children}
    </RulesContext.Provider>
  );
}
